import { FlowEventHandlers } from '../../../shared/flow-event';
import {
  LoginByOtpOneCredentialOptionalFlowEventDefinition,
  LoginByOtpOneCredentialOptionalFlowEventState,
} from './types';
import { PageName } from '@shopee-user/url';
import { appTimingUtil, monitor } from '../../../platform/monitoring';
import { BusinessPerfMetric } from '../../../platform/monitoring/performance/enums';
import {
  generateTrackingLoginView,
  generateTrackingLoginWithSMSNextButtonClick,
  generateTrackingLoginBackButtonClick,
  generateTrackingLoginHelpButtonClick,
  generateTrackingLoginSignupButtonClick,
  generateTrackingLoginWithPasswordButtonClick,
  generateTrackingLoginSuccessAction,
} from '@shopee-user/tracking-login';
import {
  generateTrackingAccountBannedContactUsButtonClick,
  generateTrackingAccountBannedOkButtonClick,
  generateTrackingAccountBannedImpression,
  TrackingDataFieldTypeOtpScenario,
} from '@shopee-user/tracking-otp-enter-verification';
import { sendLoginResult } from '@tob/shared/monitoring/business-metric';
import { AccType, BusinessPage, Status } from '@tob/shared/monitoring/enums';
import { toMonitoringLoginMethod } from '@tob/shared/monitoring/business-metric/utils';
import {
  toTrackingAccountType,
  toTrackingLoginMethod,
} from '@tob/shared/tracking/utils';

export const TRACKER_NAME = 'login-by-otp-one-credential-optional';

export type LoginByOtpOneCredentialEventState = {
  authKey: string;
};

export const createInitialState = (): LoginByOtpOneCredentialEventState => {
  return {
    authKey: '',
  };
};

export function createTracker() {
  const trackPageLoad = ({ pageName }: { pageName: string }) => {
    const softNavigationDuration = appTimingUtil.getDurationFromSoftNavigationStart();
    const isSoftNavigation = softNavigationDuration > 0;

    monitor.sendDuration({
      name: BusinessPerfMetric.PageLoad,
      duration: isSoftNavigation
        ? softNavigationDuration
        : appTimingUtil.getAppAliveDuration(),
      data: {
        soft_nav: isSoftNavigation ? 'true' : 'false',
        page_name: pageName,
        // some of custom metadatas are not allowed to be accessed in Grafana because of database performance concerns
        // so here we use status to indicate the navigation type
        status: isSoftNavigation ? 'soft_nav' : 'hard_nav',
      },
    });
  };

  const trackLoginResult = ({
    status,
    accType,
    error,
    apiSource,
  }: {
    status: Status;
    accType: AccType;
    error?: number;
    apiSource?: string;
  }) => ({
    state,
  }: {
    state: LoginByOtpOneCredentialOptionalFlowEventState;
  }) => {
    sendLoginResult({
      status,
      login_method: toMonitoringLoginMethod(state.authKey),
      business_page: BusinessPage.LoginByOtpOneCredentialOptional,
      acc_type: accType,
      error,
      api_source: apiSource,
    });
  };

  const handlers: FlowEventHandlers<
    LoginByOtpOneCredentialOptionalFlowEventDefinition,
    LoginByOtpOneCredentialOptionalFlowEventState
  > = {
    onLoginPageInit: ({ authKey }: { authKey: string }) => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginView({
            loginMethod: toTrackingLoginMethod(authKey),
          })
        );
        trackPageLoad({
          pageName: PageName.PageLoginByOTP,
        });

        return { authKey };
      };
    },
    onLoginSubmitAuthKey: ({ authKey }: { authKey: string }) => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginWithSMSNextButtonClick({
            loginMethod: toTrackingLoginMethod(authKey),
          })
        );

        return { authKey };
      };
    },
    onLoginBackClick: () => {
      return ({ state, tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginBackButtonClick({
            loginMethod: toTrackingLoginMethod(state.authKey),
          })
        );
      };
    },
    onLoginHelpClick: () => {
      return ({ state, tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginHelpButtonClick({
            loginMethod: toTrackingLoginMethod(state.authKey),
          })
        );
      };
    },
    onLoginLoginWithPasswordClick: () => {
      return ({ state, tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginWithPasswordButtonClick({
            loginMethod: toTrackingLoginMethod(state.authKey),
          })
        );
      };
    },
    onLoginSignupClick: () => {
      return ({ state, tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginSignupButtonClick({
            loginMethod: toTrackingLoginMethod(state.authKey),
          })
        );
      };
    },
    onLoginAccountBannedInit: () => {
      return ({ tools }) => {
        tools.sendImpressionEvent(
          generateTrackingAccountBannedImpression({
            scenario: TrackingDataFieldTypeOtpScenario.LoginWithSms,
          })
        );
      };
    },
    onLoginAccountBannedOkClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingAccountBannedOkButtonClick({
            scenario: TrackingDataFieldTypeOtpScenario.LoginWithSms,
          })
        );
      };
    },
    onLoginAccountBannedContactUsClick: () => {
      return ({ tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingAccountBannedContactUsButtonClick({
            scenario: TrackingDataFieldTypeOtpScenario.LoginWithSms,
          })
        );
      };
    },
    onLoginSuccess: ({ accType }: { accType: AccType }) => {
      return ({ state, tools }) => {
        tools.sendBITrackingEvent(
          generateTrackingLoginSuccessAction({
            loginMethod: toTrackingLoginMethod(state.authKey),
            accountType: toTrackingAccountType(accType),
          })
        );

        trackLoginResult({ status: Status.Success, accType })({ state });
      };
    },
    onLoginFailed: ({
      accType,
      error,
      apiSource,
    }: {
      accType: AccType;
      error: number;
      apiSource: string;
    }) => {
      return ({ state }) => {
        trackLoginResult({ status: Status.Failed, accType, error, apiSource })({
          state,
        });
      };
    },
  };
  return {
    name: TRACKER_NAME,
    handlers,
  };
}
